import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import AppTextInput from './AppTextInput';
import {
  approvalStatusLabel,
  buildRequestSnapshotFromOwner,
  canConnectStripeFromApproval,
  fetchStripeConnectApproval,
  submitStripeConnectAccessRequest,
} from '../services/stripeConnectApprovalService';
import { getMerchantTermsForApi } from '../services/merchantTermsService';
import { AuthManager } from '../utils/authManager';
import { StripeConnectApprovalRecord } from '../types/stripeConnectApproval';

export interface StripeConnectApprovalSectionProps {
  businessId: string;
  merchantTermsAccepted: boolean;
  onCanConnectChange: (canConnect: boolean) => void;
}

const StripeConnectApprovalSection: React.FC<StripeConnectApprovalSectionProps> = ({
  businessId,
  merchantTermsAccepted,
  onCanConnectChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [approval, setApproval] = useState<StripeConnectApprovalRecord | null>(null);
  const [requestNote, setRequestNote] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchStripeConnectApproval(businessId);
      const record = res.approval || { status: 'none' };
      setApproval(record);
      onCanConnectChange(canConnectStripeFromApproval(record));
      const owner = await AuthManager.getSavedBusinessOwner();
      if (owner?.id === businessId && res.approval) {
        await AuthManager.saveBusinessOwner({
          ...owner,
          stripeConnectApproval: res.approval,
        });
      }
    } catch {
      onCanConnectChange(false);
    } finally {
      setLoading(false);
    }
  }, [businessId, onCanConnectChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSubmitRequest = async () => {
    if (!merchantTermsAccepted) {
      Alert.alert(
        'Merchant terms required',
        'Accept all merchant payment terms before submitting a payment access request.'
      );
      return;
    }

    const owner = await AuthManager.getSavedBusinessOwner();
    const merchantTerms = getMerchantTermsForApi(owner);
    if (!merchantTerms) {
      Alert.alert(
        'Merchant terms required',
        'Complete and save all merchant payment term checkboxes first.'
      );
      return;
    }

    setSubmitting(true);
    try {
      const snapshot = owner ? buildRequestSnapshotFromOwner(owner) : {};
      const result = await submitStripeConnectAccessRequest(
        businessId,
        merchantTerms,
        requestNote.trim(),
        snapshot
      );
      if (result.success) {
        setApproval(result.approval || { status: 'pending_review' });
        onCanConnectChange(false);
        Alert.alert(
          'Request submitted',
          'BookAPT will review your payment access request. You will be able to connect Stripe after approval.'
        );
      } else {
        Alert.alert('Could not submit', result.error || 'Please try again.');
      }
    } finally {
      setSubmitting(false);
      await refresh();
    }
  };

  if (loading) {
    return (
      <View style={styles.box}>
        <ActivityIndicator color="#635bff" />
        <Text style={styles.loadingText}>Loading payment access status…</Text>
      </View>
    );
  }

  const status = approval?.status || 'none';
  const canConnect = canConnectStripeFromApproval(approval);

  return (
    <View style={styles.box}>
      <Text style={styles.title}>Payment access (BookAPT review)</Text>
      <Text style={styles.subtitle}>
        For now, each business must be approved before connecting Stripe. This helps keep the
        platform limited to legitimate businesses.
      </Text>

      <View style={[styles.statusPill, statusPillStyle(status)]}>
        <Text style={styles.statusPillText}>{approvalStatusLabel(status)}</Text>
      </View>

      {status === 'rejected' && approval?.rejectionReason ? (
        <Text style={styles.rejectionText}>Reason: {approval.rejectionReason}</Text>
      ) : null}

      {status === 'pending_review' ? (
        <Text style={styles.body}>
          Submitted{' '}
          {approval?.requestedAt
            ? new Date(approval.requestedAt).toLocaleString()
            : 'recently'}
          . Our team will review your request. Connect Stripe will unlock after approval.
        </Text>
      ) : null}

      {canConnect ? (
        <Text style={styles.approvedText}>
          Approved
          {approval?.approvedAt
            ? ` on ${new Date(approval.approvedAt).toLocaleDateString()}`
            : ''}
          . You may connect Stripe below.
        </Text>
      ) : null}

      {(status === 'none' || status === 'rejected') && (
        <>
          <Text style={styles.label}>Optional note for reviewers</Text>
          <AppTextInput
            style={styles.input}
            placeholder="e.g. salon at 123 Main St, active subscription"
            value={requestNote}
            onChangeText={setRequestNote}
            multiline
            maxLength={500}
          />
          <TouchableOpacity
            style={[
              styles.requestButton,
              (!merchantTermsAccepted || submitting) && styles.buttonDisabled,
            ]}
            onPress={handleSubmitRequest}
            disabled={!merchantTermsAccepted || submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.requestButtonText}>Submit payment access request</Text>
            )}
          </TouchableOpacity>
          {!merchantTermsAccepted ? (
            <Text style={styles.hint}>Complete merchant payment terms above first.</Text>
          ) : null}
        </>
      )}

      {status === 'suspended' ? (
        <Text style={styles.rejectionText}>
          Contact support@bookapt.io if you believe this is an error.
        </Text>
      ) : null}
    </View>
  );
};

function statusPillStyle(status: string) {
  switch (status) {
    case 'approved':
      return { backgroundColor: '#d4edda' };
    case 'pending_review':
      return { backgroundColor: '#fff3cd' };
    case 'rejected':
    case 'suspended':
      return { backgroundColor: '#f8d7da' };
    default:
      return { backgroundColor: '#e9ecef' };
  }
}

const styles = StyleSheet.create({
  box: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  title: {
    fontSize: 17,
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 20,
    marginBottom: 12,
  },
  loadingText: {
    marginTop: 8,
    fontSize: 14,
    color: '#6c757d',
    textAlign: 'center',
  },
  statusPill: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginBottom: 10,
  },
  statusPillText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#343a40',
  },
  body: {
    fontSize: 14,
    color: '#495057',
    lineHeight: 20,
    marginBottom: 8,
  },
  approvedText: {
    fontSize: 14,
    color: '#155724',
    fontWeight: '600',
    marginBottom: 8,
  },
  rejectionText: {
    fontSize: 14,
    color: '#721c24',
    lineHeight: 20,
    marginBottom: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#495057',
    marginBottom: 6,
    marginTop: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ced4da',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 72,
    textAlignVertical: 'top',
    marginBottom: 12,
    backgroundColor: '#f8f9fa',
  },
  requestButton: {
    backgroundColor: '#28a745',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  requestButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  hint: {
    fontSize: 13,
    color: '#856404',
    marginTop: 8,
    textAlign: 'center',
  },
});

export default StripeConnectApprovalSection;
